import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiCheckCircle,
    FiClock,
    FiEye,
    FiFileText,
    FiLoader,
    FiPlus,
    FiRefreshCw,
    FiSearch,
    FiXCircle,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
} from "react-router-dom";

import {
    confirmPurchaseBill,
    fetchPurchaseBillList,
    purchaseBillErrorSelector,
    purchaseBillListSelector,
    purchaseBillLoadingSelector,
    purchaseBillPaginationSelector,
    purchaseBillSummarySelector,
} from "../../../../../../store/billing/purchaseBills/purchaseBillSlice";

import "./PurchaseBills.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const PAGE_SIZE_OPTIONS = [
    10,
    20,
    50,
    100,
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    ).format(date);
};

const formatLabel = (
    value
) =>
    String(
        value || "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );

const getSupplierName = (
    bill
) =>
    bill
        ?.supplierSnapshot
        ?.supplierName ||
    bill
        ?.supplierSnapshot
        ?.companyName ||
    bill
        ?.supplier
        ?.supplierName ||
    bill
        ?.supplier
        ?.name ||
    "Supplier";

const getSupplierMobile = (
    bill
) =>
    bill
        ?.supplierSnapshot
        ?.mobile ||
    bill
        ?.supplier
        ?.mobile ||
    "-";

const getDocumentNumber = (
    bill
) =>
    bill
        ?.documentNumber ||
    bill
        ?.purchaseBillNumber ||
    "-";

const getGrandTotal = (
    bill
) =>
    roundMoney(
        bill
            ?.totals
            ?.grandTotal ??
        bill
            ?.grandTotal ??
        0
    );

const getPaidAmount = (
    bill
) =>
    roundMoney(
        bill
            ?.totals
            ?.paidAmount ??
        bill
            ?.paidAmount ??
        0
    );

const getDebitNoteAmount = (
    bill
) =>
    roundMoney(
        bill
            ?.totals
            ?.debitNoteAmount ??
        bill
            ?.debitNoteAmount ??
        0
    );

const getBalanceAmount = (
    bill
) =>
    roundMoney(
        bill
            ?.totals
            ?.balanceAmount ??
        bill
            ?.balanceAmount ??
        0
    );

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const PurchaseBillListPage = () => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    const records =
        useSelector(
            purchaseBillListSelector
        ) || [];

    const pagination =
        useSelector(
            purchaseBillPaginationSelector
        ) || {};

    const summary =
        useSelector(
            purchaseBillSummarySelector
        ) || {};

    const loading =
        useSelector(
            purchaseBillLoadingSelector
        ) || {};

    const errors =
        useSelector(
            purchaseBillErrorSelector
        ) || {};

    /*
    |--------------------------------------------------------------------------
    | FILTER STATE
    |--------------------------------------------------------------------------
    */

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        status,
        setStatus,
    ] = useState("");

    const [
        paymentStatus,
        setPaymentStatus,
    ] = useState("");

    const [
        fromDate,
        setFromDate,
    ] = useState("");

    const [
        toDate,
        setToDate,
    ] = useState("");

    const [
        page,
        setPage,
    ] = useState(1);

    const [
        limit,
        setLimit,
    ] = useState(20);

    const [
        actionId,
        setActionId,
    ] = useState("");

    const [
        localError,
        setLocalError,
    ] = useState("");

    /*
    |--------------------------------------------------------------------------
    | LOAD LIST
    |--------------------------------------------------------------------------
    */

    const loadPurchaseBills =
        useCallback(
            async () => {
                try {
                    setLocalError("");

                    await dispatch(
                        fetchPurchaseBillList({
                            page,
                            limit,

                            search:
                                search.trim(),

                            status,

                            paymentStatus,

                            fromDate,

                            toDate,
                        })
                    ).unwrap();
                } catch (
                    error
                ) {
                    setLocalError(
                        typeof error ===
                            "string"
                            ? error
                            : "Unable to load purchase bills."
                    );
                }
            },
            [
                dispatch,
                page,
                limit,
                search,
                status,
                paymentStatus,
                fromDate,
                toDate,
            ]
        );

    useEffect(
        () => {
            const timer =
                setTimeout(
                    () => {
                        loadPurchaseBills();
                    },
                    350
                );

            return () =>
                clearTimeout(
                    timer
                );
        },
        [
            loadPurchaseBills,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | SUMMARY
    |--------------------------------------------------------------------------
    */

    const normalizedSummary =
        useMemo(
            () => ({
                totalBills:
                    toNumber(
                        summary
                            ?.totalBills
                    ),

                totalPurchaseAmount:
                    roundMoney(
                        summary
                            ?.totalPurchaseAmount
                    ),

                totalPaidAmount:
                    roundMoney(
                        summary
                            ?.totalPaidAmount
                    ),

                totalOutstanding:
                    roundMoney(
                        summary
                            ?.totalOutstanding
                    ),

                totalDebitNoteAmount:
                    roundMoney(
                        summary
                            ?.totalDebitNoteAmount
                    ),
            }),
            [
                summary,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | VIEW
    |--------------------------------------------------------------------------
    */

    const handleView = (
        bill
    ) => {
        if (!bill?._id) {
            return;
        }

        history.push(
            `/billing/purchase-bills/${bill._id}`
        );
    };

    /*
    |--------------------------------------------------------------------------
    | CREATE
    |--------------------------------------------------------------------------
    */

    const handleCreate =
        () => {
            history.push(
                "/billing/purchase-bills/create"
            );
        };

    /*
    |--------------------------------------------------------------------------
    | EDIT
    |--------------------------------------------------------------------------
    */

    const handleEdit = (
        bill
    ) => {
        if (!bill?._id) {
            return;
        }

        history.push(
            `/billing/purchase-bills/${bill._id}/edit`
        );
    };

    /*
    |--------------------------------------------------------------------------
    | CONFIRM
    |--------------------------------------------------------------------------
    */

    const handleConfirm =
        async (
            bill
        ) => {
            if (!bill?._id) {
                return;
            }

            const approved =
                window.confirm(
                    `Confirm purchase bill ${getDocumentNumber(
                        bill
                    )}?\n\nThis will post stock and supplier ledger.`
                );

            if (!approved) {
                return;
            }

            try {
                setLocalError("");

                setActionId(
                    bill._id
                );

                await dispatch(
                    confirmPurchaseBill(
                        bill._id
                    )
                ).unwrap();

                await loadPurchaseBills();
            } catch (
                error
            ) {
                setLocalError(
                    typeof error ===
                        "string"
                        ? error
                        : "Unable to confirm purchase bill."
                );
            } finally {
                setActionId("");
            }
        };

    /*
    |--------------------------------------------------------------------------
    | CLEAR FILTERS
    |--------------------------------------------------------------------------
    */

    const handleClearFilters =
        () => {
            setSearch("");
            setStatus("");
            setPaymentStatus("");
            setFromDate("");
            setToDate("");
            setPage(1);
        };

    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    const totalRecords =
        toNumber(
            pagination?.total
        );

    const totalPages =
        Math.max(
            toNumber(
                pagination
                    ?.totalPages,
                1
            ),
            1
        );

    const currentPage =
        Math.max(
            toNumber(
                pagination
                    ?.page,
                page
            ),
            1
        );

    const startRecord =
        totalRecords >
        0
            ? (
                  currentPage -
                  1
              ) *
                  limit +
              1
            : 0;

    const endRecord =
        Math.min(
            currentPage *
                limit,
            totalRecords
        );

    return (
        <div className="purchase-bill-page">
            {/* HEADER */}

            <header className="purchase-bill-header">
                <div className="purchase-bill-heading">
                    <span className="purchase-bill-heading-icon">
                        <FiFileText />
                    </span>

                    <div>
                        <h1>
                            Purchase Bills
                        </h1>

                        <p>
                            Manage supplier purchases, stock receipts, payments and debit notes.
                        </p>
                    </div>
                </div>

                <div className="purchase-bill-header-actions">
                    <button
                        type="button"
                        className="purchase-bill-button"
                        disabled={
                            loading.list
                        }
                        onClick={
                            loadPurchaseBills
                        }
                    >
                        <FiRefreshCw
                            className={
                                loading.list
                                    ? "purchase-bill-spinner"
                                    : ""
                            }
                        />

                        Refresh
                    </button>

                    <button
                        type="button"
                        className="purchase-bill-button purchase-bill-button-primary"
                        onClick={
                            handleCreate
                        }
                    >
                        <FiPlus />

                        New Purchase Bill
                    </button>
                </div>
            </header>

            {/* SUMMARY */}

            <section className="purchase-bill-summary-grid">
                <div className="purchase-bill-summary-card">
                    <span>
                        Total Bills
                    </span>

                    <strong>
                        {
                            normalizedSummary
                                .totalBills
                        }
                    </strong>

                    <small>
                        Purchase documents
                    </small>
                </div>

                <div className="purchase-bill-summary-card purchase-bill-summary-purchase">
                    <span>
                        Purchase Amount
                    </span>

                    <strong>
                        {formatCurrency(
                            normalizedSummary
                                .totalPurchaseAmount
                        )}
                    </strong>

                    <small>
                        Total billed value
                    </small>
                </div>

                <div className="purchase-bill-summary-card purchase-bill-summary-paid">
                    <span>
                        Paid
                    </span>

                    <strong>
                        {formatCurrency(
                            normalizedSummary
                                .totalPaidAmount
                        )}
                    </strong>

                    <small>
                        Supplier payments
                    </small>
                </div>

                <div className="purchase-bill-summary-card purchase-bill-summary-outstanding">
                    <span>
                        Outstanding
                    </span>

                    <strong>
                        {formatCurrency(
                            normalizedSummary
                                .totalOutstanding
                        )}
                    </strong>

                    <small>
                        Current payable
                    </small>
                </div>
            </section>

            {/* FILTERS */}

            <section className="purchase-bill-toolbar">
                <div className="purchase-bill-search">
                    <FiSearch />

                    <input
                        type="text"
                        value={
                            search
                        }
                        placeholder="Search bill, supplier, invoice or mobile..."
                        onChange={(
                            event
                        ) => {
                            setPage(1);

                            setSearch(
                                event
                                    .target
                                    .value
                            );
                        }}
                    />
                </div>

                <select
                    value={
                        status
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(1);

                        setStatus(
                            event
                                .target
                                .value
                        );
                    }}
                >
                    <option value="">
                        All Status
                    </option>

                    <option value="DRAFT">
                        Draft
                    </option>

                    <option value="CONFIRMED">
                        Confirmed
                    </option>

                    <option value="CANCELLED">
                        Cancelled
                    </option>
                </select>

                <select
                    value={
                        paymentStatus
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(1);

                        setPaymentStatus(
                            event
                                .target
                                .value
                        );
                    }}
                >
                    <option value="">
                        All Payment Status
                    </option>

                    <option value="UNPAID">
                        Unpaid
                    </option>

                    <option value="PARTIALLY_PAID">
                        Partially Paid
                    </option>

                    <option value="PAID">
                        Paid
                    </option>
                </select>

                <input
                    type="date"
                    value={
                        fromDate
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(1);

                        setFromDate(
                            event
                                .target
                                .value
                        );
                    }}
                />

                <input
                    type="date"
                    value={
                        toDate
                    }
                    min={
                        fromDate ||
                        undefined
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(1);

                        setToDate(
                            event
                                .target
                                .value
                        );
                    }}
                />

                <button
                    type="button"
                    className="purchase-bill-button"
                    onClick={
                        handleClearFilters
                    }
                >
                    Clear
                </button>
            </section>

            {/* ERROR */}

            {(localError ||
                errors.list ||
                errors.confirm) && (
                <div className="purchase-bill-alert">
                    {localError ||
                        errors.list ||
                        errors.confirm}
                </div>
            )}

            {/* TABLE */}

            <section className="purchase-bill-card">
                {loading.list &&
                records.length ===
                    0 ? (
                    <div className="purchase-bill-empty">
                        <FiLoader className="purchase-bill-spinner" />

                        <h3>
                            Loading purchase bills...
                        </h3>
                    </div>
                ) : records.length ===
                  0 ? (
                    <div className="purchase-bill-empty">
                        <FiFileText />

                        <h3>
                            No purchase bills found
                        </h3>

                        <p>
                            Create your first purchase bill to start tracking supplier purchases.
                        </p>
                    </div>
                ) : (
                    <div className="purchase-bill-table-wrapper">
                        <table className="purchase-bill-table">
                            <thead>
                                <tr>
                                    <th>
                                        Purchase Bill
                                    </th>

                                    <th>
                                        Supplier
                                    </th>

                                    <th>
                                        Supplier Invoice
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                    <th className="text-right">
                                        Amount
                                    </th>

                                    <th className="text-right">
                                        Paid
                                    </th>

                                    <th className="text-right">
                                        Debit Note
                                    </th>

                                    <th className="text-right">
                                        Balance
                                    </th>

                                    <th>
                                        Payment
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th className="text-right">
                                        Action
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {records.map(
                                    (
                                        bill,
                                        index
                                    ) => {
                                        const documentStatus =
                                            String(
                                                bill
                                                    ?.documentStatus ||
                                                    bill
                                                        ?.status ||
                                                    "DRAFT"
                                            ).toUpperCase();

                                        const currentPaymentStatus =
                                            String(
                                                bill
                                                    ?.paymentStatus ||
                                                    "UNPAID"
                                            ).toUpperCase();

                                        const processing =
                                            actionId ===
                                            bill?._id;

                                        return (
                                            <tr
                                                key={
                                                    bill
                                                        ?._id ||
                                                    index
                                                }
                                            >
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="purchase-bill-link"
                                                        onClick={() =>
                                                            handleView(
                                                                bill
                                                            )
                                                        }
                                                    >
                                                        {getDocumentNumber(
                                                            bill
                                                        )}
                                                    </button>
                                                </td>

                                                <td>
                                                    <div className="purchase-bill-supplier">
                                                        <strong>
                                                            {getSupplierName(
                                                                bill
                                                            )}
                                                        </strong>

                                                        <span>
                                                            {getSupplierMobile(
                                                                bill
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>

                                                <td>
                                                    {bill
                                                        ?.supplierInvoiceNumber ||
                                                        "-"}
                                                </td>

                                                <td>
                                                    {formatDate(
                                                        bill
                                                            ?.documentDate
                                                    )}
                                                </td>

                                                <td className="text-right purchase-bill-amount">
                                                    {formatCurrency(
                                                        getGrandTotal(
                                                            bill
                                                        )
                                                    )}
                                                </td>

                                                <td className="text-right purchase-bill-paid">
                                                    {formatCurrency(
                                                        getPaidAmount(
                                                            bill
                                                        )
                                                    )}
                                                </td>

                                                <td className="text-right purchase-bill-debit-note">
                                                    {formatCurrency(
                                                        getDebitNoteAmount(
                                                            bill
                                                        )
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    <strong
                                                        className={
                                                            getBalanceAmount(
                                                                bill
                                                            ) >
                                                            0
                                                                ? "purchase-bill-balance-due"
                                                                : "purchase-bill-balance-clear"
                                                        }
                                                    >
                                                        {formatCurrency(
                                                            getBalanceAmount(
                                                                bill
                                                            )
                                                        )}
                                                    </strong>
                                                </td>

                                                <td>
                                                    <span
                                                        className={`purchase-bill-payment-status purchase-bill-payment-status-${currentPaymentStatus.toLowerCase()}`}
                                                    >
                                                        {currentPaymentStatus ===
                                                        "PAID" ? (
                                                            <FiCheckCircle />
                                                        ) : currentPaymentStatus ===
                                                          "PARTIALLY_PAID" ? (
                                                            <FiClock />
                                                        ) : (
                                                            <FiXCircle />
                                                        )}

                                                        {formatLabel(
                                                            currentPaymentStatus
                                                        )}
                                                    </span>
                                                </td>

                                                <td>
                                                    <span
                                                        className={`purchase-bill-status purchase-bill-status-${documentStatus.toLowerCase()}`}
                                                    >
                                                        {documentStatus ===
                                                        "CONFIRMED" ? (
                                                            <FiCheckCircle />
                                                        ) : documentStatus ===
                                                          "CANCELLED" ? (
                                                            <FiXCircle />
                                                        ) : (
                                                            <FiClock />
                                                        )}

                                                        {formatLabel(
                                                            documentStatus
                                                        )}
                                                    </span>
                                                </td>

                                                <td className="text-right">
                                                    <div className="purchase-bill-actions">
                                                        <button
                                                            type="button"
                                                            className="purchase-bill-view-button"
                                                            disabled={
                                                                processing
                                                            }
                                                            onClick={() =>
                                                                handleView(
                                                                    bill
                                                                )
                                                            }
                                                        >
                                                            <FiEye />

                                                            View
                                                        </button>

                                                        {documentStatus ===
                                                            "DRAFT" && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="purchase-bill-edit-button"
                                                                    disabled={
                                                                        processing
                                                                    }
                                                                    onClick={() =>
                                                                        handleEdit(
                                                                            bill
                                                                        )
                                                                    }
                                                                >
                                                                    Edit
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="purchase-bill-confirm-button"
                                                                    disabled={
                                                                        processing
                                                                    }
                                                                    onClick={() =>
                                                                        handleConfirm(
                                                                            bill
                                                                        )
                                                                    }
                                                                >
                                                                    {processing ? (
                                                                        <FiLoader className="purchase-bill-spinner" />
                                                                    ) : (
                                                                        <FiCheckCircle />
                                                                    )}

                                                                    Confirm
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* PAGINATION */}

                <footer className="purchase-bill-pagination">
                    <div>
                        <span>
                            Showing{" "}
                            <strong>
                                {
                                    startRecord
                                }
                            </strong>
                            {" - "}
                            <strong>
                                {
                                    endRecord
                                }
                            </strong>
                            {" of "}
                            <strong>
                                {
                                    totalRecords
                                }
                            </strong>
                        </span>

                        <select
                            value={
                                limit
                            }
                            onChange={(
                                event
                            ) => {
                                setPage(1);

                                setLimit(
                                    Number(
                                        event
                                            .target
                                            .value
                                    )
                                );
                            }}
                        >
                            {PAGE_SIZE_OPTIONS.map(
                                (
                                    size
                                ) => (
                                    <option
                                        key={
                                            size
                                        }
                                        value={
                                            size
                                        }
                                    >
                                        {size}
                                    </option>
                                )
                            )}
                        </select>
                    </div>

                    <div>
                        <button
                            type="button"
                            disabled={
                                currentPage <=
                                    1 ||
                                loading.list
                            }
                            onClick={() =>
                                setPage(
                                    Math.max(
                                        currentPage -
                                            1,
                                        1
                                    )
                                )
                            }
                        >
                            Previous
                        </button>

                        <span>
                            Page{" "}
                            <strong>
                                {
                                    currentPage
                                }
                            </strong>{" "}
                            of{" "}
                            <strong>
                                {
                                    totalPages
                                }
                            </strong>
                        </span>

                        <button
                            type="button"
                            disabled={
                                currentPage >=
                                    totalPages ||
                                loading.list
                            }
                            onClick={() =>
                                setPage(
                                    Math.min(
                                        currentPage +
                                            1,
                                        totalPages
                                    )
                                )
                            }
                        >
                            Next
                        </button>
                    </div>
                </footer>
            </section>
        </div>
    );
};

export default PurchaseBillListPage;